import "./lib/sentry";
import { enableMapSet } from "immer";
import { BrowserRouter, Route, Routes } from "react-router";
import { Toaster } from "sonner";
import EditorPage from "./features/Editor/Editor";
import Videos from "./features/Videos/Videos";
import AuthGuard from "./features/AuthGuard";
import AppShell from "./lib/components/AppShell/AppShell";
import RootRedirect from "./features/RootRedirect";
import HomePage from "./features/Home/HomePage";
import TutorialsPage from "./features/Home/TutorialsPage";
import { QueryClientProvider } from "@tanstack/react-query";
import { ClerkProvider } from "@clerk/react";

import { queryClient } from "./lib/queries/query-client";
import { getEnv } from "./lib/utils";
import SignInPage from "./features/SignIn";
import TeamGuard from "./features/TeamGuard";
import Setup from "./features/Setup/Setup";
import SignOut from "./features/SignOut";
import Teams from "./features/Teams/Teams";
import Invite from "./features/Invite/Invite";
import UserSessionTracker from "./lib/components/UserSessionTracker";
import NotFound from "./features/NotFound";
import IntercomMessenger from "./features/Intercom/IntercomMessenger";
import ClientConfigGuard from "./lib/components/ClientConfigGuard";
import DfyOrdersPage from "./features/DoneForYou/DfyOrdersPage";
import DfyOrderDetailsPage from "./features/DoneForYou/DfyOrderDetailsPage";
import DfyOrderFormPage from "./features/DoneForYou/OrderForm/DfyOrderFormPage";
import DfyEditorOrdersPage from "./features/DoneForYou/DfyEditorOrdersPage";
import DfyEditorOrderDetailsPage from "./features/DoneForYou/DfyEditorOrderDetailsPage";
import { ThirdPartyVideoPage } from "./features/ThirdPartyVideo/ThirdPartyVideoPage";
import IconGradient from "./lib/components/IconGradient";
import NotificationsPoller from "./lib/components/NotificationsPoller";

enableMapSet();

declare global {
  interface Window {
    Clerk: any;
  }
}

export const paths = {
  root: () => "/",
  home: (tId: string) => `/teams/${tId}/home`,
  tutorials: (tId: string) => `/teams/${tId}/tutorials`,
  signIn: () => "/sign-in",
  signUp: () => "/sign-up",
  videos: (tId: string) => `/teams/${tId}/videos`,
  videosFolder: (tId: string, folderId: string) =>
    `/teams/${tId}/videos/folders/${folderId}`,
  team: (tId: string) => `/teams/${tId}/settings`,
  videoEdit: (tId: string, vId: string) => `/teams/${tId}/videos/${vId}/edit`,
  dfy: (tId: string) => `/teams/${tId}/done-for-you`,
  dfyOrder: (tId: string, orderId: string) =>
    `/teams/${tId}/done-for-you/${orderId}`,
  dfyOrderForm: (tId: string, orderId: string) =>
    `/teams/${tId}/done-for-you/${orderId}/edit`,
  dfyEditorOrders: (tId: string) => `/teams/${tId}/orders`,
  dfyEditorOrder: (tId: string, orderId: string) =>
    `/teams/${tId}/orders/${orderId}`,
  invite: (inviteId: string) => `/invite/${inviteId}/accept`,
  thirdPartyVideoPublic: (token: string) => `/tp/video?token=${token}`,
  thirdPartyVideoTeam: (tId: string, token: string) =>
    `/teams/${tId}/tp/video?token=${token}`,
};

export const externalPaths = {
  featureRequests: () => "https://amplifiles.featurebase.app",
};

export default function App() {
  return (
    <ClerkProvider
      publishableKey={getEnv("VITE_CLERK_PUBLISHABLE_KEY")}
      signInUrl="/sign-in"
      signUpUrl="/sign-up"
      signInFallbackRedirectUrl="/"
      signUpFallbackRedirectUrl="/"
      afterSignOutUrl="/sign-in"
      supportEmail="hello@amplifiles.ai"
    >
      <IconGradient />
      <QueryClientProvider client={queryClient}>
        <Toaster />
        <ClientConfigGuard>
          <BrowserRouter>
            <UserSessionTracker />
            <IntercomMessenger />

            <Routes>
              <Route path="/sign-in" element={<SignInPage type="sign-in" />} />
              <Route path="/sign-up" element={<SignInPage type="sign-up" />} />
              <Route path="/sign-out" element={<SignOut />} />
              <Route
                path="/invite/:inviteExternalId/accept"
                element={<Invite />}
              />
              <Route
                path="/tp/video"
                element={<ThirdPartyVideoPage key="tp-video-public" isPublic />}
              />

              <Route element={<AuthGuard />}>
                <Route path="/" element={<RootRedirect />} />
                <Route path="/setup" element={<Setup />} />
                <Route path="/teams/:teamExternalId" element={<TeamGuard />}>
                  <Route element={<NotificationsPoller />}>
                    <Route element={<AppShell />}>
                      <Route
                        path="/teams/:teamExternalId/home"
                        element={<HomePage />}
                      />
                      <Route
                        path="/teams/:teamExternalId/tutorials"
                        element={<TutorialsPage />}
                      />
                      <Route
                        path="/teams/:teamExternalId/settings"
                        element={<Teams />}
                      />
                      <Route
                        path="/teams/:teamExternalId/videos"
                        element={<Videos />}
                      />
                      <Route
                        path="/teams/:teamExternalId/videos/folders/:folderExternalId"
                        element={<Videos />}
                      />
                      <Route
                        path="/teams/:teamExternalId/done-for-you"
                        element={<DfyOrdersPage />}
                      />
                      <Route
                        path="/teams/:teamExternalId/done-for-you/:orderExternalId"
                        element={<DfyOrderDetailsPage />}
                      />
                      <Route
                        path="/teams/:teamExternalId/done-for-you/:orderExternalId/edit"
                        element={<DfyOrderFormPage />}
                      />
                      <Route
                        path="/teams/:teamExternalId/orders"
                        element={<DfyEditorOrdersPage />}
                      />
                      <Route
                        path="/teams/:teamExternalId/orders/:orderExternalId"
                        element={<DfyEditorOrderDetailsPage />}
                      />
                      <Route path="*" element={<NotFound />} />
                    </Route>
                    <Route
                      path="/teams/:teamExternalId/videos/:videoExternalId/edit"
                      element={<EditorPage />}
                    />
                    <Route
                      path="/teams/:teamExternalId/tp/video"
                      element={
                        <ThirdPartyVideoPage
                          key="tp-video-team"
                          isPublic={false}
                        />
                      }
                    />
                  </Route>
                </Route>
                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
          </BrowserRouter>
        </ClientConfigGuard>
      </QueryClientProvider>
    </ClerkProvider>
  );
}
